import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Kart, Rozet } from "@koclukpanel/ui";
import { AbonelikKarti } from "../Finans";
import {
  abonelikGetir,
  type AbonelikGorunumu,
  type PaketHaklari as Haklar,
  type Periyot,
} from "../api";

/**
 * PAKET HAKLARI — Öğrenci 360° (sürüm 9a; UNİKOÇ öğrenci detayı "Paket / Koçluk" paneli; F-BIL-01)
 *
 * Yeni uç YOK: haklar abonelik ucunun cevabında (`haklar`) geliyor ve o uç `finans.abonelik.ozet` /
 * `finans.tahsilat.goruntule` + öğrenci kapsamıyla korunuyor. Sekme de aynı izinlere bağlı
 * (`sekmeler.tsx` "paket"); izni olmayan sekmeyi görmez, uç da 403 verir — bölüm hiç çizilmez.
 *
 * Sorgu anahtarı `AbonelikKarti` ile AYNI (`["abonelik", ogrenciId]`): iki bileşen tek istek paylaşıyor.
 *
 * Salt okunur: haklar PAKET tanımından gelir; öğrenci başına istisna yok (UNİKOÇ: "Bu alanlar yalnızca
 * yönetici düzenleyebilir"). Kullanım (kaç saat özel ders yapıldı) tutulmuyor — "kalan" uydurulmuyor.
 */

const PERIYOT_ADI: Record<Periyot, string> = {
  MONTHLY: "Aylık",
  QUARTERLY: "3 aylık",
  SEMIANNUAL: "6 aylık",
  ANNUAL: "Yıllık",
  CUSTOM: "Özel süre",
};

function VarYok({ acik }: { acik: boolean }) {
  return <Rozet ton={acik ? "onay" : "notr"}>{acik ? "Var" : "Yok"}</Rozet>;
}

export function PaketHaklariKarti({ haklar, bitis }: { haklar: Haklar; bitis: string }) {
  const satirlar: Array<{ etiket: string; deger: ReactNode }> = [
    { etiket: "Özel ders hakkı", deger: <VarYok acik={haklar.ozelDers} /> },
    {
      etiket: "Özel ders saat hakkı",
      deger: haklar.ozelDersSaati === null ? "—" : `${haklar.ozelDersSaati} saat`,
    },
    { etiket: "Soru çözüm grubu hakkı", deger: <VarYok acik={haklar.soruCozumGrubu} /> },
    { etiket: "Kaynak kitap gönderimi", deger: <VarYok acik={haklar.kitapGonderimi} /> },
    {
      etiket: "Koçluk süresi",
      deger: `${PERIYOT_ADI[haklar.periyot]} · ${new Date(bitis).toLocaleDateString("tr-TR", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "Europe/Istanbul",
      })} bitiş`,
    },
  ];
  return (
    <Kart
      baslik="Paket hakları"
      baslikSeviyesi={3}
      aciklama="Haklar paket tanımından gelir; yalnızca yönetici değiştirebilir."
    >
      <dl className="divide-notr-100 flex flex-col divide-y">
        {satirlar.map((s) => (
          <div key={s.etiket} className="flex items-center justify-between gap-3 py-2 text-sm">
            <dt className="text-metin-ikincil">{s.etiket}</dt>
            <dd className="text-metin-koyu text-right font-medium">{s.deger}</dd>
          </div>
        ))}
      </dl>
    </Kart>
  );
}

/** Abonelik verisinden hak kartı; abonelik yoksa ya da sunucu hak göndermediyse HİÇBİR ŞEY çizilmez. */
export function PaketHaklari({ ogrenciId }: { ogrenciId: string }) {
  const { data } = useQuery<AbonelikGorunumu | null>({
    queryKey: ["abonelik", ogrenciId],
    queryFn: () => abonelikGetir(ogrenciId),
  });
  if (!data?.haklar) return null;
  return <PaketHaklariKarti haklar={data.haklar} bitis={data.bitis} />;
}

/** "Paket ve Ödeme" sekmesinin gövdesi: önce haklar, sonra mevcut abonelik kartı (değişmedi). */
export function PaketBolumu({ ogrenciId, acabilir }: { ogrenciId: string; acabilir: boolean }) {
  return (
    <div className="flex flex-col gap-4">
      <PaketHaklari ogrenciId={ogrenciId} />
      <AbonelikKarti ogrenciId={ogrenciId} acabilir={acabilir} />
    </div>
  );
}
